import {
	Component,
	OnInit,
	OnDestroy,
	inject,
	ElementRef,
	HostListener,
	ChangeDetectorRef,
	EventEmitter,
	Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, distinctUntilChanged, filter, switchMap, tap, catchError, of } from 'rxjs';
import { MiddlewareApiService, SearchResult, SearchResultKind } from '../api/middleware-api.service';
import { SiteService } from '../site-selector/site.service';
import { PoiDetailService } from '../poi-detail.service';
import { PoiTypeSettingsService } from '../settings/poi-type-settings.service';
import { getConfig } from '../config.service';

import './search.component.scss';

@Component({
	selector: 'app-search',
	standalone: true,
	imports: [CommonModule, FormsModule],
	template: `
		<div class="search-container">
			<div class="search-input-container">
				<input
					type="text"
					[(ngModel)]="searchTerm"
					(ngModelChange)="onSearchTermChange($event)"
					(focus)="onInputFocus()"
				/>
				<img
					*ngIf="loading"
					src="assets/images/tail-spin.svg"
					class="search-loading"
					alt="Loading..."
				/>
				<img
					*ngIf="!loading && searchTerm.length > 0"
					src="assets/images/icn_clear_input_grey.svg"
					class="search-clear"
					(click)="clearSearch()"
					alt="Clear"
				/>
			</div>
			<div class="search-result-container" *ngIf="showResults && searchResults.length > 0">
				<ul>
					<li *ngFor="let item of searchResults" (click)="selectResult(item)">
						<div class="clickable">
							<h4>
								<img *ngIf="item.icon" [src]="item.icon" width="24" alt="" />
								{{ item.title }}
							</h4>
							<p>{{ item.description }}</p>
						</div>
					</li>
				</ul>
			</div>
		</div>
	`,
	styles: []
})
export class SearchComponent implements OnInit, OnDestroy {
	@Output() resultSelected = new EventEmitter<SearchResult>();

	searchTerm = '';
	searchResults: SearchResult[] = [];
	loading = false;
	showResults = false;

	private searchTermSubject = new Subject<string>();
	private searchSubscription: Subscription | null = null;
	private cachedResults: SearchResult[] = [];
	private cachedTerm = '';

	private api = inject(MiddlewareApiService);
	private siteService = inject(SiteService);
	private poiDetailService = inject(PoiDetailService);
	private poiTypeSettings = inject(PoiTypeSettingsService);
	private elementRef = inject(ElementRef);
	private cdr = inject(ChangeDetectorRef);

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (!this.elementRef.nativeElement.contains(event.target)) {
			this.showResults = false;
		}
	}

	ngOnInit(): void {
		this.searchSubscription = this.searchTermSubject.pipe(
			debounceTime(1000),
			distinctUntilChanged(),
			filter(term => term.length >= 2),
			tap(() => {
				this.loading = true;
				this.cdr.detectChanges();
			}),
			switchMap(term => {
				const siteId = this.siteService.selectedSite?.id;
				if (!siteId) {
					console.warn('[Search] No site selected');
					return of([]);
				}
				return this.api.searchPois(siteId, term).pipe(
					catchError(error => {
						console.error('[Search] Error:', error);
						return of([]);
					})
				);
			})
		).subscribe(results => {
			this.processResults(results);
			this.loading = false;
			this.showResults = true;
			this.cdr.detectChanges();
		});
	}

	ngOnDestroy(): void {
		this.searchSubscription?.unsubscribe();
	}

	onSearchTermChange(term: string): void {
		if (term.length < 2) {
			this.searchResults = [];
			this.showResults = false;
			return;
		}
		this.searchTermSubject.next(term);
	}

	onInputFocus(): void {
		if (this.cachedResults.length > 0 && this.searchTerm === this.cachedTerm) {
			this.searchResults = this.cachedResults;
			this.showResults = true;
		}
	}

	clearSearch(): void {
		this.searchTerm = '';
		this.searchResults = [];
		this.showResults = false;
		this.loading = false;
	}

	private processResults(results: SearchResult[]): void {
		// Filter out Device Revision results (sourceKind === 4) - Phase 7 dependency
		const filtered = results.filter(r => r.sourceKind !== SearchResultKind.DeviceRevision);

		// Enrich with icons
		this.enrichResultsWithIcons(filtered);

		this.searchResults = filtered;
		this.cachedResults = filtered;
		this.cachedTerm = this.searchTerm;
	}

	private enrichResultsWithIcons(results: SearchResult[]): void {
		for (const result of results) {
			if (result.sourceTypeId) {
				result.icon = this.getIconUrl(result.sourceTypeId);
			}
		}
	}

	private getIconUrl(poiTypeId: number): string {
		return `${getConfig().api.middlewareUrl}/api/NavvisImageHandler?imagepath=poi${poiTypeId}`;
	}

	async selectResult(item: SearchResult): Promise<void> {
		this.showResults = false;
		this.resultSelected.emit(item);

		// Use skomap internal site ID for FMS/middleware APIs
		const siteId = this.siteService.selectedSite?.id;

		switch (item.sourceKind) {
			case SearchResultKind.Building:
				if (siteId) await this.poiDetailService.selectBuilding(siteId, item.sourceId);
				break;

			case SearchResultKind.FunctionalArea:
				if (siteId) await this.poiDetailService.selectFunctionalArea(siteId, item.sourceId);
				break;

			case SearchResultKind.StaticPoi:
				// Navigate to the POI (unhighlight, go, highlight) and open its info panel
				await this.poiDetailService.selectPoi(item.sourceId);
				break;

			default:
				console.warn('[Search] Unhandled source kind:', item.sourceKind);
		}
	}
}
